/**
 * Stuck-Zoom Watchdog (mobile only)
 *
 * The viewport meta's `maximum-scale=1` suppresses iOS Safari's automatic
 * input-focus zoom, but it does NOT clamp zoom that arrives through the
 * user-gesture carve-outs: a pinch scale restored across a refresh, the
 * orientation-change zoom bug, or an accidental pinch during two-thumb play.
 * Over a fullscreen game every one of those is a trap — the canvas swallows
 * the touch gestures that would normally pinch back out, so the page stays
 * zoomed with no user-reachable exit.
 *
 * The watchdog detects a visual-viewport scale stuck above 1 and snaps it
 * back by rewriting the viewport meta with a matching `minimum-scale=1`
 * floor — min = max forces WebKit to re-clamp the current scale to exactly
 * 1 — then restores the original content shortly after. Trade-off: on
 * mobile, a deliberate accessibility pinch-zoom also snaps back once the
 * fingers lift; the game being playable always beats a zoom state nobody
 * can escape.
 *
 * Resets are deferred while any finger is down (never fight an in-progress
 * gesture) and are purely event-driven — no polling. Every touch release
 * re-checks, so a clamp that loses a race (or an iOS version that ignores
 * one attempt) retries on the very next tap — a zoomed page can never go
 * permanently quiet.
 *
 * Install only when `device.mobile`: desktop trackpad/browser zoom is
 * deliberate, user-reversible, and none of our business.
 */

import { PINCH_SCALE_TOLERANCE } from '../game/Constants';

/** How long the min-scale clamp stays applied before the original viewport
 *  meta is restored. One frame is not reliably enough for WebKit to process
 *  the rewrite; 150ms is imperceptible and comfortably past the re-layout. */
const CLAMP_RESTORE_MS = 150;

/** Staggered re-checks after boot/pageshow/orientationchange. Those moments
 *  can report their final scale AFTER the event fires (WebKit settles the
 *  visual viewport asynchronously), so a single immediate check can read a
 *  stale 1. Not polling — a bounded three-shot verification per event. */
const SETTLE_CHECK_DELAYS_MS = [0, 100, 300];

/**
 * Wires up the stuck-zoom watchdog. Call once at boot on mobile platforms.
 *
 * Returns a teardown function — important under Vite HMR where this module
 * may re-execute, and useful for tests. In normal production boot the return
 * value can be ignored.
 */
export function initZoomGuard(): () => void {
  const viewport = window.visualViewport;
  const meta = document.querySelector<HTMLMetaElement>('meta[name="viewport"]');
  if (!viewport || !meta) {
    return () => {};
  }

  // Captured once from our own index.html meta, which never contains a
  // minimum-scale — so appending one below cannot produce a duplicate key.
  const normalContent = meta.content;
  let activeTouches = 0;
  let restoreTimer: number | null = null;
  let settleTimers: number[] = [];

  const forceScaleReset = () => {
    if (restoreTimer !== null) return; // a clamp is already in flight
    meta.content = `${normalContent}, minimum-scale=1`;
    restoreTimer = window.setTimeout(() => {
      restoreTimer = null;
      meta.content = normalContent;
    }, CLAMP_RESTORE_MS);
  };

  const check = () => {
    if (activeTouches > 0) return; // let the gesture finish — release re-checks
    if (viewport.scale <= PINCH_SCALE_TOLERANCE) return;
    forceScaleReset();
  };

  /** See SETTLE_CHECK_DELAYS_MS. Clears the previous batch so event storms
   *  (rapid rotations) coalesce instead of accumulating timers. */
  const scheduleSettleChecks = () => {
    for (const timer of settleTimers) window.clearTimeout(timer);
    settleTimers = SETTLE_CHECK_DELAYS_MS.map((delay) => window.setTimeout(check, delay));
  };

  const handleTouchStart = (e: TouchEvent) => {
    activeTouches = e.touches.length;
  };

  const handleTouchSettle = (e: TouchEvent) => {
    activeTouches = e.touches.length;
    if (activeTouches === 0) check();
  };

  // Scale changes surface as visual-viewport resizes (scroll added as a
  // cheap belt for coalesced pan+scale updates); pageshow covers bfcache
  // restores where no resize fires. Lifecycle/orientation events get the
  // staggered settle checks instead of a single immediate read.
  viewport.addEventListener('resize', check);
  viewport.addEventListener('scroll', check);
  window.addEventListener('pageshow', scheduleSettleChecks);
  window.addEventListener('orientationchange', scheduleSettleChecks);
  window.addEventListener('touchstart', handleTouchStart, { passive: true, capture: true });
  window.addEventListener('touchend', handleTouchSettle, { passive: true, capture: true });
  window.addEventListener('touchcancel', handleTouchSettle, { passive: true, capture: true });

  // Boot checks — catch a pinch scale iOS restored across a refresh (the
  // "zoomed in out of nowhere after reload" stuck case), including one
  // applied a beat after our init.
  scheduleSettleChecks();

  return () => {
    viewport.removeEventListener('resize', check);
    viewport.removeEventListener('scroll', check);
    window.removeEventListener('pageshow', scheduleSettleChecks);
    window.removeEventListener('orientationchange', scheduleSettleChecks);
    window.removeEventListener('touchstart', handleTouchStart, true);
    window.removeEventListener('touchend', handleTouchSettle, true);
    window.removeEventListener('touchcancel', handleTouchSettle, true);
    for (const timer of settleTimers) window.clearTimeout(timer);
    settleTimers = [];
    if (restoreTimer !== null) {
      window.clearTimeout(restoreTimer);
      restoreTimer = null;
      meta.content = normalContent;
    }
  };
}
